import { useCallback, useEffect, useRef } from 'react';
import { Message } from '@/types/message.types';
import { useMessagesStore } from './useMessagesStore';
import { useSignalR } from './useSignalR';

function getIsMessage(value: unknown): value is Message {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  if ('id' in value && 'messageChecksum' in value) {
    return true;
  }

  return false;
}

export const useMessageSubscription = () => {
  const { actions: messagesActions, messagesMap } = useMessagesStore();
  const { subscribe, unsubscribe, isConnected } = useSignalR();

  // use ref to access latest messagesMap and actions
  const messagesMapRef = useRef(messagesMap);
  const messagesActionsRef = useRef(messagesActions);

  messagesMapRef.current = messagesMap;
  messagesActionsRef.current = messagesActions;

  const handleMessageReceived = useCallback((message: unknown) => {
    if (getIsMessage(message)) {
      const currentMessagesMap = messagesMapRef.current;
      const currentActions = messagesActionsRef.current;

      if (currentMessagesMap.has(message.id)) {
        // for messages that is not newly send by end user, id is the identifier
        currentActions.updateMessage(message.id, message);
      } else if (currentMessagesMap.has(message.messageChecksum)) {
        // for messages that is newly send by end user, optimistic messageChecksum is the identifier
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...messageWithoutId } = message;
        currentActions.updateMessage(message.messageChecksum, messageWithoutId);
      } else {
        currentActions.appendMessage(message);
      }
    }
  }, []);

  useEffect(() => {
    if (!isConnected) {
      unsubscribe('onMessageReceived', handleMessageReceived);
      return;
    }

    subscribe('onMessageReceived', handleMessageReceived);

    return () => {
      unsubscribe('onMessageReceived', handleMessageReceived);
    };
  }, [subscribe, unsubscribe, isConnected, handleMessageReceived]);
};
